import React, { useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { useHeader, useHeaderLinks } from 'providers/AreasProvider';
import { Box, Grid } from '@mui/material';
import { Typography, IconButton, Autocomplete, Button, Card } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { FilterList, AssignmentInd, GridOn, ArrowBack } from '@mui/icons-material';
import ResponsibleChangeOfferListFilters from './ResponsibleChangeOfferListFilters';
import ResponsibleChangeOfferList from './ResponsibleChangeOfferList';
import { loadStates } from 'actions/actionCreators/dictionariesActions';
import { loadResponsibleChangeOffers, bulkResponsibleChangeOffers, refreshResponsibleOffers } from '../actionCreators';
import { getResponsibleChangeOffers } from '../selectors';
import documentTypeIds from 'constants/documentTypeIds';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { SALES_RESPONSIBLES_QUERY } from '../queries';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { loadDealers } from 'actions/actionCreators/dictionariesActions';
import { debounce } from 'lodash';
import ResponsibleChangeOfferListContainerAuthorization from './ResponsibleChangeOfferListContainerAuthorization';
import * as visibilityLevels from 'constants/visibilityLevels';

const defaultOfferState = { stateId: null, name: null };
const defaultDealer = { dealerId: null, name: null };
const defaultFilters = {
  offerNumber: '',
  clientName: '',
  isAssigned: false,
  offerState: defaultOfferState,
  dealer: defaultDealer
};

const ResponsibleChangeOfferListContainer = ({
  t,
  responsibleChangeOffers,
  history,
  actions,
  filters,
  onFiltersChanged,
  pager,
  totalCount,
  onChangePage,
  onChangeRowsPerPage,
  loading,
  userInfoDealer
}) => {
  const [responsibleId, setResponsibleId] = useState(null);
  const [responsibleName, setResponsibleName] = useState(null);
  const [checkAllOffers, setCheckAllOffers] = useState(false);

  const [selectedOfferList, setSelectedOfferList] = useState([]);

  const [saving, setSaving] = useState(false);

  const [, setHeader] = useHeader();
  const [, setHeaderLinks] = useHeaderLinks();
  const [salesResponsibleOptions, setSalesResponsibleOptions] = useState([]);

  useQueryWithErrorHandling(SALES_RESPONSIBLES_QUERY, {
    onCompleted: data => {
      const responsibles = data?.automaticUserAllocationsResponsiblesByUserGroups;
      const salesResponsibles = responsibles?.salesResponsibles;
      if (salesResponsibles)
        setSalesResponsibleOptions(
          salesResponsibles.map(x => {
            return { responsibleId: x.personId, name: `${x.firstName} ${x.lastName}` };
          })
        );
    }
  });

  const handleBackToList = useCallback(() => {
    history.push('/offers');
  }, [history]);

  const handleSelectAllOffers = useCallback(() => {
    const offerList =
      responsibleChangeOffers.length > 0
        ? responsibleChangeOffers.map(offer => ({
            documentId: offer.documentId,
            siteId: offer.siteId,
            offerNumber: offer.offerNumber
          }))
        : [];
    setSelectedOfferList([...selectedOfferList, ...offerList]);
    setCheckAllOffers(true);
  }, [responsibleChangeOffers, setSelectedOfferList, selectedOfferList]);

  const handleDeselectAll = useCallback(() => {
    setCheckAllOffers(false);
    setSelectedOfferList([]);
  }, []);

  const handleCheckAll = useCallback(() => {
    if (!checkAllOffers) handleSelectAllOffers();
    else handleDeselectAll();
  }, [checkAllOffers, handleSelectAllOffers, handleDeselectAll]);

  const handleAllocateOffers = useCallback(
    (item, value) => {
      if (value === true) {
        setSelectedOfferList([...selectedOfferList, { documentId: item.documentId, siteId: item.siteId, offerNumber: item.offerNumber }]);
      } else if (value === false) {
        const newList = [...selectedOfferList];
        newList.forEach((elem, idx) => {
          if (elem.documentId === item.documentId && elem.siteId === item.siteId) newList.splice(idx, 1);
        });
        setSelectedOfferList(newList);
      }
    },
    [selectedOfferList]
  );

  const loadStates = useCallback(() => {
    return actions.loadStates(documentTypeIds.Offer);
  }, [actions]);

  const loadDealer = debounce(
    useCallback(
      input => {
        if (userInfoDealer) return Promise.resolve([userInfoDealer]);

        if (!input) {
          return Promise.resolve([]);
        }
        return actions.loadDealers(input);
      },
      [actions, userInfoDealer]
    ),
    500,
    { leading: true }
  );

  const handleResponsibleChange = useCallback(
    value => {
      setResponsibleId(value);
      setResponsibleName(salesResponsibleOptions.find(x => x.responsibleId === value)?.name || null);
    },
    [salesResponsibleOptions]
  );

  const handleResponsibleSave = useCallback(() => {
    setSaving(true);
    actions.bulkResponsibleChangeOffers(responsibleId, responsibleName, selectedOfferList).then(() => {
      setSaving(false);
    });
    setResponsibleId(null);
    setResponsibleName(null);
    setSelectedOfferList([]);
    setCheckAllOffers(false);
  }, [actions, responsibleId, responsibleName, selectedOfferList]);

  const handleRefresh = useCallback(() => {
    actions.refreshResponsibleOffers(filters, pager.pageSize);
  }, [actions, filters, pager]);

  useEffect(() => {
    setHeader(
      <Typography variant='subtitle1' fontWeight='bold'>
        {t('OfferResponsibleChange.Title')}
      </Typography>
    );
  }, [t, setHeader]);

  useEffect(() => {
    setHeaderLinks(
      <Box px={1}>
        <IconButton
          id='offerResponsibleChange_backToListBtn'
          color='primary'
          variant='text'
          onClick={handleBackToList}
          tooltip={t('OfferAction.BackToList')}
        >
          <ArrowBack color='inherit' />
        </IconButton>
      </Box>
    );
  }, [t, setHeaderLinks, handleBackToList]);

  useEffect(() => {
    const { dealer, offerState, ...rest } = filters;
    const dealerId = dealer?.dealerId;
    const stateId = offerState?.stateId;

    actions.loadResponsibleChangeOffers({ ...rest, dealerId, stateId, isFromResponsibleChange: true, visibilityLevelId : visibilityLevels.AllDocuments }, pager.page, pager.pageSize);
  }, [actions, filters, pager.page, pager.pageSize]);

  return (
    <Grid container>
      <Card icon={FilterList} title={t('OfferResponsibleChange.FiltersTitle')}>
        <ResponsibleChangeOfferListFilters
          loadStates={loadStates}
          filters={filters}
          onChangeFilterValue={onFiltersChanged}
          loadDealer={loadDealer}
          userInfoDealer={userInfoDealer}
        />
      </Card>
      <Card
        icon={GridOn}
        title={
          <Typography fontWeight='bold' variant='subtitle1'>
            {t('OfferResponsibleChange.SetResponsible')}
            {selectedOfferList.length > 0
              ? selectedOfferList.map((o, index) => {
                  if (index !== selectedOfferList.length - 1) {
                    return o.offerNumber + ', ';
                  } else return `${o.offerNumber}`;
                })
              : ''}
          </Typography>
        }
      >
        <>
          <Grid container justifyContent='space-between' alignItems='center'>
            <Grid item flexGrow={1} maxWidth='500px'>
              <Autocomplete
                id={'offerResponsibleChange_responsible'}
                fullWidth
                value={responsibleId || null}
                onChange={handleResponsibleChange}
                label={t('OfferResponsibleChange.Responsible')}
                valueKey='responsibleId'
                labelKey='name'
                simpleValue
                options={salesResponsibleOptions}
                isClearable
                isSearchable
              />
            </Grid>
            <Grid item>
              <Button
                id={'offerResponsibleChange_allocBtn'}
                onClick={handleResponsibleSave}
                disabled={responsibleId && selectedOfferList.length > 0 && !saving ? false : true}
              >
                <AssignmentInd />
                {saving ? t('OfferResponsibleChange.Allocating') : t('OfferResponsibleChange.Allocate')}
              </Button>
            </Grid>
          </Grid>
          <>
            {loading ? (
              <WrappedFakeText lines={10} my={2} />
            ) : (
              responsibleChangeOffers.length > 0 && (
                <ResponsibleChangeOfferList
                  responsibleChangeOffers={responsibleChangeOffers}
                  onAllocateOffers={handleAllocateOffers}
                  handleCheckAll={handleCheckAll}
                  checkAllOffers={checkAllOffers}
                  selectedOfferList={selectedOfferList}
                />
              )
            )}
            <PaginationWrapper
              count={totalCount}
              pageSize={pager.pageSize}
              page={pager.page}
              onRowsPerPageChange={onChangeRowsPerPage}
              onPageChange={onChangePage}
              onRefresh={handleRefresh}
            />
          </>
        </>
      </Card>
    </Grid>
  );
};

ResponsibleChangeOfferListContainer.propTypes = {
  history: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  responsibleChangeOffers: PropTypes.array.isRequired,
  pager: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  onFiltersChanged: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  totalCount: PropTypes.number.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  userInfoDealer: PropTypes.object,
  userInfoExtraInfoModel: PropTypes.object
};

function mapStateToProps(state, ownProps) {
  const states = state.dictionaries.states;

  const { filters, pager } = ownProps;

  const userInfoDealer = state.userInfo.userExtraInfoModel.isSearchingRestrictedDealer
    ? { dealerId: state.userInfo.userExtraInfoModel.dealerId, name: state.userInfo.userExtraInfoModel.dealerName }
    : null;

  const { dealer, offerState, ...rest } = filters;
  const {
    loading,
    content: responsibleChangeOffers,
    totalCount
  } = getResponsibleChangeOffers({ ...rest, dealerId: dealer?.dealerId, stateId: offerState.stateId, isFromResponsibleChange : true, visibilityLevelId: visibilityLevels.AllDocuments  }, pager.page, pager.pageSize, state);
  return { responsibleChangeOffers, totalCount, states, loading, userInfoDealer };
}

function wrapperMapStateToProps(state) {
  const userInfoExtraInfoModel = state.userInfo.userExtraInfoModel;

  return { userInfoExtraInfoModel };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { loadDealers, loadStates, loadResponsibleChangeOffers, bulkResponsibleChangeOffers, refreshResponsibleOffers },
      dispatch
    )
  };
}

const ResponsibleChangeOfferListContainerConnected = withRouter(
  compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(ResponsibleChangeOfferListContainer)
);

const ResponsibleChangeOfferListContainerWrapper = ({ userInfoExtraInfoModel }) => {
  const [pager, setPager] = useState({ page: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    ...defaultFilters,
    userPartnerId: userInfoExtraInfoModel.userPartnerId,
    dealer: {
      dealerId: userInfoExtraInfoModel.dealerId,
      name: userInfoExtraInfoModel.dealerName
    }
  });

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === 'pageSize' ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const onChangePage = page => {
    setPager(pagerUpdater('page', ++page));
  };

  const onChangeRowsPerPage = pageSize => {
    setPager(pagerUpdater('pageSize', pageSize));
  };

  const handleFiltersChanged = useCallback(
    (name, value) => {
      if (value == null) {
        setFilters(prev => ({ ...prev, [name]: { ...defaultFilters[name] } }));
        return;
      }

      setFilters(prev => ({ ...prev, [name]: value }));
      setPager(pagerUpdater('page', 1));
    },
    [pagerUpdater]
  );

  return (
    <ResponsibleChangeOfferListContainerAuthorization>
      <ResponsibleChangeOfferListContainerConnected
        filters={filters}
        pager={pager}
        onFiltersChanged={handleFiltersChanged}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </ResponsibleChangeOfferListContainerAuthorization>
  );
};

ResponsibleChangeOfferListContainerWrapper.propTypes = {
  userInfoExtraInfoModel: PropTypes.object
};

export default compose(connect(wrapperMapStateToProps))(ResponsibleChangeOfferListContainerWrapper);
